import React from 'react';
import {
  getCheckedValue,
  getOptionByValue,
  getOptionsByName,
  OptionSwitch,
  PopupRow,
  RsDropdown,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersUtils';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { Checkbox } from '@els/els-react--checkbox';
import find from 'lodash/find';
import { PREDICTED_PROCESSING_TIME_INTERVALS, ProjectSynthesisPartner } from '@Pages/RetroSynthesis/constants';
import { getPredictedProcessingTimeIntervalUpdatedValue, getPredictedProcessingTimeLabelByValue } from '@Pages/RetroSynthesis/utils';
import {
  PREDICTED_PARAMS_BBLIBS_LABEL,
  PREDICTED_PARAMS_DIVERSE_OF_ROUTES_LABEL,
  PREDICTED_PARAMS_NUMBER_OF_ROUTES_LABEL,
  PREDICTED_PARAMS_PROCESSING_TIME_LABEL,
} from './constants';
import HeaderInfoIcon from '../ProjectRoutes/components/HeaderInfoIcon';
import HeaderInfoTooltip from '../ProjectRoutes/components/HeaderInfoTooltip';
/* eslint-disable react/no-unescaped-entities */
const FULL_SYNTHESIS_PLAN = 'RX2_PREDICTION_PLAN_SHOW_FULL_STEPS';
const SYNTHESIS_PLAN_LENGTH = 'RX2_PREDICTION_PLAN_DEPTH';
const IDENTICAL_REACTIONS_STEPS = 'RX2_PREDICTION_PLAN_STEPS';
const IDENTICAL_BUILDING_BLOCKS = 'RX2_PREDICTION_PLAN_BLOCKS';
const PROCESSING_TIME = 'RX2_PREDICTION_PLAN_PROCESSING_TIME';
export const USED_BUILDING_BLOCKS = 'RX2_PREDICTION_PLAN_LIBRARIES';

export const extractDefaultPredictedParametersPai = (resp) => ({
  numberOfPlans: getCheckedValue(SYNTHESIS_PLAN_LENGTH, resp),
  identicalReactionsSteps: getCheckedValue(IDENTICAL_REACTIONS_STEPS, resp),
  identicalBuildingBlocks: getCheckedValue(IDENTICAL_BUILDING_BLOCKS, resp),
  fullSynthesisPlan: getCheckedValue(FULL_SYNTHESIS_PLAN, resp) === OptionSwitch.ON,
  processingTime: getPredictedProcessingTimeIntervalUpdatedValue(parseInt(getCheckedValue(PROCESSING_TIME, resp), 10)),
  // eslint-disable-next-line no-use-before-define
  usedBuildingBlocks: extractSelectedBuildingBlocks(USED_BUILDING_BLOCKS, resp),
  partner: ProjectSynthesisPartner.PAI,
});

export const getBlockArrayValues = (optionName, resp = []) => {
  // eslint-disable-next-line no-shadow
  const optionSet = find(resp, (optionSet) => optionSet && optionSet.name === optionName) || {};
  const values =
    optionSet.preferences &&
    optionSet.preferences.map((element) => ({
      name: element.name,
      label: element.label,
      values: element.values,
    }));
  return values || [];
};

export const extractSelectedBuildingBlocks = (optionName, resp = []) => {
  // eslint-disable-next-line no-shadow
  const optionSet = find(resp, (optionSet) => optionSet && optionSet.name === optionName) || {};
  const values =
    (optionSet.preferences &&
      optionSet.preferences.filter((element) => {
        if (!element.values) {
          return false;
        }

        /* If option has only one value - use this value,
         * if more than one - check if "checked" is defined:
         *   if yes - use checked
         *   if no - use defaultValue
         */
        const activeValue =
          element.values.length === 1
            ? element.values[0]
            : element.values.find((value) => (!isUndefined(value.checked) ? value.checked : value.defaultValue));
        return activeValue && activeValue.value === OptionSwitch.ON;
      })) ||
    [];
  return values.map((element) => element.name) || [];
};

function PredictedParametersPai({ optionResponse, onParamsChanged, projectParams, editable }) {
  if (isEmpty(optionResponse) || isEmpty(projectParams)) {
    return null;
  }

  const buildingBlockElements = getBlockArrayValues(USED_BUILDING_BLOCKS, optionResponse);
  const processingTime = getPredictedProcessingTimeIntervalUpdatedValue(projectParams.processingTime);
  return (
    <>
      {editable && (
        <>
          <PopupRow className="rs-column-subheader mgt">
            Length and depth of routes
            <HeaderInfoIcon
              tooltipId="info-tooltip-predicted-length"
              e2eName="info-tooltip-predicted-length"
              ariaLabel={PREDICTED_PARAMS_NUMBER_OF_ROUTES_LABEL}
              informationIconTitle={PREDICTED_PARAMS_NUMBER_OF_ROUTES_LABEL}
            />
            <HeaderInfoTooltip id="info-tooltip-predicted-length">
              <div className="rs-tooltip-big__content">
                <ul className="info-tooltip-content-list">
                  <li className="info-tooltip-content-list-item bullet-dot">
                    The option "Full route" will create routes from the target molecule to available building blocks. You have the ability
                    to select the maximum No of routes to be generated between 20 to 100 by using the drop down. The default is 20. Please
                    note: building block libraries (see below) have a large influence on the route prediction process. Depending on the
                    selected library the No of plans generated and their lengths (No of steps per plan) may vary significantly.
                  </li>
                  <li className="info-tooltip-content-list-item bullet-dot">
                    The option "Last step only" will create synthesis suggestions for a given target for idea generation, but only show the
                    last step of a full route, without taking building block availability into account. The list of last step reaction can
                    become very high (several hundred suggestion). When scanning through the suggestions you may select the ones you want to
                    investigate further by clicking the corresponding starting material and select "Synthesize".
                  </li>
                </ul>
              </div>
            </HeaderInfoTooltip>
          </PopupRow>
          <PopupRow>
            <RadioButtonGroup.Option
              e2eName="checkbox-fullSynthesisPlan-yes"
              checked={!!projectParams.fullSynthesisPlan}
              label={<span>Full routes: </span>}
              onChange={(selected) => {
                if (selected) {
                  onParamsChanged({ fullSynthesisPlan: true });
                }
              }}
            />

            <RsDropdown
              options={getOptionsByName(SYNTHESIS_PLAN_LENGTH, optionResponse)}
              value={getOptionByValue(SYNTHESIS_PLAN_LENGTH, (projectParams.numberOfPlans || '').toString(), optionResponse)}
              onChange={(option) => {
                const numberOfPlans = option && parseInt(option.value, 10);
                onParamsChanged({ numberOfPlans });
              }}
              e2eName="numberOfPlansOptions"
            />
          </PopupRow>
          <PopupRow className="mgt">
            <RadioButtonGroup.Option
              e2eName="checkbox-fullSynthesisPlan-no"
              checked={!projectParams.fullSynthesisPlan}
              label={<span>Last step only</span>}
              onChange={(selected) => {
                if (selected) {
                  onParamsChanged({ fullSynthesisPlan: false });
                }
              }}
            />
          </PopupRow>
          <hr className="grey-line" />
          <PopupRow className="rs-column-subheader">
            Diversity of routes
            <HeaderInfoIcon
              tooltipId="info-tooltip-predicted-diversity"
              e2eName="info-tooltip-predicted-diversity"
              ariaLabel={PREDICTED_PARAMS_DIVERSE_OF_ROUTES_LABEL}
              informationIconTitle={PREDICTED_PARAMS_DIVERSE_OF_ROUTES_LABEL}
            />
            <HeaderInfoTooltip id="info-tooltip-predicted-diversity">
              <div className="rs-tooltip-big__content">
                The tool will consider the settings "occurrence of same reaction step" and "occurrence of same Building Block" to filter all
                predicted plans for a given target molecules accordingly. This will result in a shorter, but more diverse list of routes.
              </div>
            </HeaderInfoTooltip>
          </PopupRow>
          <PopupRow className="rs-subrow mgt">Allow</PopupRow>
          <PopupRow className="rs-subrow">
            <RsDropdown
              options={getOptionsByName(IDENTICAL_REACTIONS_STEPS, optionResponse)}
              value={getOptionByValue(IDENTICAL_REACTIONS_STEPS, (projectParams.identicalReactionsSteps || '').toString(), optionResponse)}
              onChange={(option) => {
                const identicalReactionsSteps = option && parseInt(option.value, 10);
                onParamsChanged({ identicalReactionsSteps });
              }}
              e2eName="identicalReactionsSteps"
            />
            identical reaction steps per project
          </PopupRow>
          <PopupRow className="rs-subrow">
            <RsDropdown
              options={getOptionsByName(IDENTICAL_BUILDING_BLOCKS, optionResponse)}
              value={getOptionByValue(IDENTICAL_BUILDING_BLOCKS, (projectParams.identicalBuildingBlocks || '').toString(), optionResponse)}
              onChange={(option) => {
                const identicalBuildingBlocks = option && parseInt(option.value, 10);
                onParamsChanged({ identicalBuildingBlocks });
              }}
              e2eName="identicalBuildingBlocks"
            />
            identical building blocks per project
          </PopupRow>
          <hr className="grey-line" />
          <PopupRow className="rs-column-subheader">
            Processing time
            <HeaderInfoIcon
              tooltipId="info-tooltip-predicted-processing"
              e2eName="info-tooltip-predicted-processing"
              ariaLabel={PREDICTED_PARAMS_PROCESSING_TIME_LABEL}
              informationIconTitle={PREDICTED_PARAMS_PROCESSING_TIME_LABEL}
            />
            <HeaderInfoTooltip id="info-tooltip-predicted-processing">
              <div className="rs-tooltip-big__content">
                As the retrosynthesis process can work virtually indefinitely, a processing time limit has to be defined. By the Standard
                option, retrosynthesis computes predictive results for approximately 15 minutes. In case no results have been found with the
                standard option you can set the processing time to Extended, which will run for approximately 30 minutes.
              </div>
            </HeaderInfoTooltip>
          </PopupRow>
          <PopupRow className="mgt">
            {PREDICTED_PROCESSING_TIME_INTERVALS.map((interval) => (
              <RadioButtonGroup.Option
                key={interval.value}
                e2eName={`checkbox-processingTime-${interval.value}`}
                className="checkbox_inline_right"
                checked={interval.value === processingTime}
                label={<span>{interval.label}</span>}
                onChange={(selected) => {
                  if (selected) {
                    onParamsChanged({ processingTime: interval.value });
                  }
                }}
              />
            ))}
          </PopupRow>
          <hr className="grey-line" />
          <PopupRow className="rs-column-subheader">
            Select Building Block Libraries
            <HeaderInfoIcon
              tooltipId="info-tooltip-predicted-building-blocks"
              e2eName="info-tooltip-predicted-building-blocks"
              ariaLabel={PREDICTED_PARAMS_BBLIBS_LABEL}
              informationIconTitle={PREDICTED_PARAMS_BBLIBS_LABEL}
            />
            <HeaderInfoTooltip id="info-tooltip-predicted-building-blocks">
              <div className="rs-tooltip-big__content">
                You may select all or single building block libraries to be considered in the generation of routes. If a route branch
                reaches a Building Block then further propagation is stopped. Please note: If you enter known Reaxys substances as target
                molecules and you have selected "Reaxys common substrates" as Building Block library, then the tool will typically predict
                quite short routes, because they reach the stop criterion very early. You may deselect the Reaxys library to get alternative
                results.
              </div>
            </HeaderInfoTooltip>
          </PopupRow>
          {buildingBlockElements.map((element) => (
            <PopupRow className="mgt" key={element.name}>
              <Checkbox
                className="checkbox_no-padding bb"
                disabled={element.values.length === 1}
                checked={projectParams.usedBuildingBlocks.indexOf(element.name) >= 0}
                onChange={(event, isChecked) => {
                  onParamsChanged({
                    usedBuildingBlocks: buildingBlockElements
                      .filter(
                        (block) =>
                          (element.name === block.name && isChecked) ||
                          (element.name !== block.name && projectParams.usedBuildingBlocks.indexOf(block.name) >= 0)
                      )
                      .map((block) => block.name),
                  });
                }}
                label={`${element.label} (${element.name})`}
                e2eName="checkbox-building-block"
              />
            </PopupRow>
          ))}
        </>
      )}
      {!editable && (
        <div className="rs-readonly-section">
          <div className="rs-readonly-row numberOfPlans">
            {projectParams.fullSynthesisPlan && (
              <span className="rs-readonly-row__label">
                <b>{projectParams.numberOfPlans}</b> full routes (up to)
              </span>
            )}
            {!projectParams.fullSynthesisPlan && <span className="rs-readonly-row__label">Last step only</span>}
          </div>
          <div className="rs-readonly-row identicalReactionsSteps">
            <span className="rs-readonly-row__label">
              <b>{projectParams.identicalReactionsSteps}</b> identical reaction steps per project (up to)
            </span>
          </div>
          <div className="rs-readonly-row identicalBuildingBlocks">
            <span className="rs-readonly-row__label">
              <b>{projectParams.identicalBuildingBlocks}</b> identical building blocks per project (up to)
            </span>
          </div>
          <div className="rs-readonly-row processingTime">
            <span className="rs-readonly-row__label">
              <b>{getPredictedProcessingTimeLabelByValue(projectParams.processingTime)}</b> processing time
            </span>
          </div>
          <div className="rs-readonly-row usedBuildingBlocks">
            <span className="rs-readonly-row__label">
              <b>{projectParams.usedBuildingBlocks.length ? projectParams.usedBuildingBlocks.join(' ') : 'No'}</b> building blocks
            </span>
          </div>
        </div>
      )}
    </>
  );
}

export default PredictedParametersPai;
